import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IInitialState, RootState } from '../../../reduxService';
import * as BeMaccaferriIdentityHelper from '../../api/identity/BeMaccaferriIdentityHelper';
import { isProductionEnv, isDevelopmentEnv } from '../../utils/utils';
import { selectUsername } from '../auth/authSlice';
import { hasResponse } from '../../utils/typeGuards';

const initialState: IInitialState<IUser> = {
    initialState: true,
};

export interface IUser {
    firstName?: string;
    lastName?: string;
    email?: string;
    isAdmin: boolean;
}

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(fetchUser.pending, state => {
                state.data = undefined;
                state.pending = true;
                state.error = undefined;
                state.initialState = false;
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.data = action.payload;
                state.pending = false;
                state.error = undefined;
                state.initialState = false;
            })
            .addCase(fetchUser.rejected, (state, action) => {
                state.data = undefined;
                state.pending = false;
                const payload = action.payload as Record<string, unknown> | undefined;
                state.error = {
                    status: (payload && typeof payload === 'object' && typeof payload.status === 'number') ? payload.status : 0,
                    data: (payload && typeof payload === 'object' && typeof payload.data === 'string') ? payload.data : 'Network error',
                };
                state.initialState = false;
            })
            .addCase(fetchLogout.fulfilled, state => {
                state.data = undefined;
                state.pending = false;
                state.error = undefined;
                state.initialState = true;
            });
    },
});

export const fetchUser = createAsyncThunk('EDesignApi/GetUser', async (_undefined, { rejectWithValue, getState }) => {
    let user: IUser;
    try {
        if (isProductionEnv() || isDevelopmentEnv()) {
            const resUserName = await BeMaccaferriIdentityHelper.getUserNameAsync();
            const resUserEmail = await BeMaccaferriIdentityHelper.getUserEmailAsync();
            user = {
                lastName: resUserName.data.last_Name,
                firstName: resUserName.data.first_Name,
                email: resUserEmail.data.email,
                isAdmin: resUserName.data.isAdmin == 1,
            };
        } else {
            // In development mode, use the username from auth if available
            const state = getState() as RootState;
            const authUsername = selectUsername(state);

            user = {
                lastName: '',
                firstName: authUsername || 'copdev',
                email: 'developmentUser@email.com',
                isAdmin: true,
            };
        }
        return user;
    } catch (error: unknown) {
        if (hasResponse(error) && (error.response.status === 401 || error.response.status === 403)) {
            window.location.href = process.env.REACT_APP_EDESIGN_PORTAL_URL ?? '';
        }
        return rejectWithValue(hasResponse(error) ? error.response : error);
    }
});

export const fetchLogout = createAsyncThunk('EDesignApi/Logout', async (_undefined, { rejectWithValue }) => {
    try {
        await BeMaccaferriIdentityHelper.logoutAsync();
    } catch (error: unknown) {
        return rejectWithValue(hasResponse(error) ? error.response : error);
    }
});

export const selectUser = (state: RootState) => state.user;

export default userSlice.reducer;
